import React, { useEffect } from 'react';
import { StyleSheet, Animated } from 'react-native';

import { Button } from './button';
import { Text } from './text';

import { useTheme } from '@/components/theme-provider';

export interface SnackbarProps {
	visible: boolean;
	onDismiss: () => void;
	action?: {
		label: string;
		onPress: () => void;
	};
	children: React.ReactNode;
	duration?: number;
}

export const Snackbar: React.FC<SnackbarProps> = ({
	visible,
	onDismiss,
	action,
	children,
	duration = 4000,
}) => {
	const theme = useTheme();
	const translateY = React.useRef(new Animated.Value(100)).current;

	useEffect(() => {
		if (visible) {
			Animated.timing(translateY, {
				toValue: 0,
				duration: 300,
				useNativeDriver: true,
			}).start();

			const timer = setTimeout(() => {
				onDismiss();
			}, duration);

			return () => clearTimeout(timer);
		} else {
			Animated.timing(translateY, {
				toValue: 100,
				duration: 300,
				useNativeDriver: true,
			}).start();
		}
	}, [visible, duration, onDismiss, translateY]);

	if (!visible) return null;

	return (
		<Animated.View
			style={[
				styles.container,
				{
					backgroundColor: theme.colors.inverseSurface,
					transform: [{ translateY }],
				},
			]}
		>
			<Text style={{ color: theme.colors.onInverseSurface, flex: 1 }}>
				{children}
			</Text>
			{action && (
				<Button
					mode="text"
					onPress={action.onPress}
					textColor={theme.colors.inversePrimary}
					compact
				>
					{action.label}
				</Button>
			)}
		</Animated.View>
	);
};

const styles = StyleSheet.create({
	container: {
		position: 'absolute',
		bottom: 0,
		left: 0,
		right: 0,
		flexDirection: 'row',
		alignItems: 'center',
		padding: 16,
		elevation: 6,
		zIndex: 1000,
	},
});
